import { useState } from 'react';
import { motion } from 'motion/react';
import {
  Mail,
  Lock,
  CheckCircle2,
  Loader2,
  ArrowLeft,
  RotateCw,
  ShieldCheck,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { cognitoConfirmSignup, cognitoResendCode } from '../lib/cognito-auth';
import { toast } from 'sonner';

interface ConfirmEmailPageProps {
  onNavigate: (page: string) => void;
  onLoginSuccess: () => void;
}

export function ConfirmEmailPage({ onNavigate, onLoginSuccess }: ConfirmEmailPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { user } = await cognitoConfirmSignup(email, code, password);
      setIsSuccess(true);
      toast.success(`Email verified! Welcome, ${user.name}!`, {
        description: 'Your account is now active',
      });
      // Auto-navigate after a short delay
      setTimeout(() => {
        onLoginSuccess();
      }, 2000);
    } catch (err: any) {
      logger.error('Email confirmation error', err as Error, undefined, 'ConfirmEmailPage');
      setError(err.message || 'Verification failed. Please check your code and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || !email) return;
    setResendLoading(true);

    try {
      await cognitoResendCode(email);
      toast.success('Verification code resent!', {
        description: `A new code has been sent to ${email}`,
      });
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      logger.error('Resend code error', err as Error, undefined, 'ConfirmEmailPage');
      toast.error('Failed to resend code', {
        description: err.message || 'Please try again later',
      });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Card className="border-border/50 bg-card/95 backdrop-blur-xl shadow-2xl border-2 border-[#FDB022]/20">
          <CardHeader className="text-center space-y-2">
            {!isSuccess ? (
              <>
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
                  className="mx-auto h-16 w-16 rounded-full bg-[#FDB022]/10 flex items-center justify-center"
                >
                  <Mail className="h-8 w-8 text-[#FDB022]" />
                </motion.div>
                <CardTitle className="text-2xl font-bold">Confirm Your Email</CardTitle>
                <CardDescription>
                  Enter the verification code sent to your email address to activate your account
                </CardDescription>
              </>
            ) : (
              <>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                  className="mx-auto h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center"
                >
                  <ShieldCheck className="h-8 w-8 text-green-500" />
                </motion.div>
                <CardTitle className="text-2xl font-bold text-green-500">Email Verified!</CardTitle>
                <CardDescription>
                  Your account has been confirmed. Redirecting you now...
                </CardDescription>
              </>
            )}
          </CardHeader>

          <CardContent className="space-y-4">
            {!isSuccess ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="space-y-4"
              >
                <div className="rounded-lg bg-[#FDB022]/10 border border-[#FDB022]/30 p-3">
                  <p className="text-sm text-[#FDB022]">
                    We sent a 6-digit verification code to your email. Please check your inbox (and spam folder) and enter it below.
                  </p>
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleConfirm} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="confirm-email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="confirm-email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        required
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="confirm-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10"
                        required
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        data-testid="confirm-email-toggle-password-visibility"
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Enter the password you used when creating your account
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirm-code">Verification Code</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="confirm-code"
                        type="text"
                        placeholder="Enter 6-digit code"
                        value={code}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                          setCode(val);
                        }}
                        className="pl-10 text-center text-lg tracking-[0.5em] font-mono"
                        required
                        maxLength={6}
                        autoComplete="one-time-code"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#FDB022] hover:bg-[#FDB022]/90 text-black font-semibold"
                    disabled={isLoading || code.length < 6 || !email || !password}
                    data-testid="confirm-email-submit"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Confirm Email & Sign In
                      </>
                    )}
                  </Button>
                </form>

                {/* Resend code */}
                <div className="flex items-center justify-center gap-1 text-sm">
                  <span className="text-muted-foreground">Didn't receive the code?</span>
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={resendLoading || resendCooldown > 0 || !email}
                    className="text-[#FDB022] hover:underline font-medium disabled:opacity-50 disabled:no-underline inline-flex items-center gap-1"
                    data-testid="confirm-email-resend-code"
                  >
                    {resendLoading ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <RotateCw className="w-3 h-3" />
                    )}
                    {resendLoading
                      ? 'Sending...'
                      : resendCooldown > 0
                        ? `Resend in ${resendCooldown}s`
                        : 'Resend Code'}
                  </button>
                </div>

                {/* Back to login */}
                <button
                  type="button"
                  onClick={() => onNavigate('home')}
                  className="flex items-center justify-center gap-1.5 w-full text-sm text-muted-foreground hover:text-foreground transition-colors pt-1"
                  data-testid="confirm-email-back-to-home"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back to Home
                </button>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-4"
              >
                <Loader2 className="w-6 h-6 animate-spin text-green-500 mx-auto" />
                <p className="text-sm text-muted-foreground mt-2">Redirecting...</p>
              </motion.div>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>All authentication is managed through AWS Cognito</p>
        </div>
      </motion.div>
    </div>
  );
}
